// /admin/usuarios-admin — gestión de cuentas administrador (super-admin only).
//
// Tabla con admins (activos + desactivados) + acciones por fila (editar,
// desactivar/reactivar). Header con botón "Invitar nuevo admin".

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Shield, UserPlus, Mail, FileText, AlertCircle, Loader2, Edit2, Power, RefreshCw, CheckCircle2 } from 'lucide-react';

import {
  listUsuariosAdmin, invitarAdmin,
  desactivarAdminUser, reactivarAdminUser, updateAdminUser,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

function localePara(lang) {
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return map[lang] || 'es-ES';
}

function fmtFecha(iso, lang) {
  if (!iso) return '—';
  const d = new Date(iso);
  return new Intl.DateTimeFormat(localePara(lang), {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(d);
}

function fmtFechaHora(iso, lang) {
  if (!iso) return '—';
  const d = new Date(iso);
  return new Intl.DateTimeFormat(localePara(lang), {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

export default function AdminUsuariosAdmin() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null); // {tipo: 'ok'|'err', texto}

  const [showInvitar, setShowInvitar] = useState(false);
  const [editing, setEditing] = useState(null); // user objeto

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await listUsuariosAdmin();
      setData(d);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Auto-clear banner
  useEffect(() => {
    if (!banner) return;
    const id = setTimeout(() => setBanner(null), 4500);
    return () => clearTimeout(id);
  }, [banner]);

  const handleDesactivar = async (u) => {
    if (!window.confirm(t('admin.usuarios_admin.confirmar_desactivar', { nombre: u.nombre || u.email }))) return;
    try {
      await desactivarAdminUser(u.id);
      setBanner({ tipo: 'ok', texto: t('admin.usuarios_admin.desactivado_ok') });
      cargar();
    } catch (e) {
      const msg = e instanceof ApiError
        ? (t('admin.usuarios_admin.errores.' + e.code, { defaultValue: '' }) || e.message)
        : t('auth.errores.generico');
      setBanner({ tipo: 'err', texto: msg });
    }
  };

  const handleReactivar = async (u) => {
    try {
      await reactivarAdminUser(u.id);
      setBanner({ tipo: 'ok', texto: t('admin.usuarios_admin.reactivado_ok') });
      cargar();
    } catch (e) {
      const msg = e instanceof ApiError
        ? (t('admin.usuarios_admin.errores.' + e.code, { defaultValue: '' }) || e.message)
        : t('auth.errores.generico');
      setBanner({ tipo: 'err', texto: msg });
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-titulo">
            <Shield size={22} strokeWidth={1.5} /> {t('admin.usuarios_admin.titulo')}
          </h1>
          <p className="admin-page-sub">{t('admin.usuarios_admin.sub')}</p>
        </div>
        <div className="admin-filtros-inline">
          <Link to="/admin/usuarios-admin/invitaciones" className="btn-secondary btn-mini">
            <Mail size={14} /> {t('admin.usuarios_admin.btn_invitaciones')}
          </Link>
          <Link to="/admin/usuarios-admin/audit-log" className="btn-secondary btn-mini">
            <FileText size={14} /> {t('admin.usuarios_admin.btn_audit')}
          </Link>
          <button type="button" className="btn-primary btn-mini" onClick={() => setShowInvitar(true)}>
            <UserPlus size={14} /> {t('admin.usuarios_admin.btn_invitar')}
          </button>
        </div>
      </header>

      {banner && (
        <div className={banner.tipo === 'ok' ? 'admin-msg-ok' : 'modal-error-general'} style={{ marginBottom: 16 }}>
          {banner.tipo === 'ok' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{banner.texto}</span>
        </div>
      )}

      {loading && (
        <div className="admin-loading"><Loader2 size={20} className="spin" /> <span>{t('admin.cargando')}</span></div>
      )}
      {error && (
        <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>
      )}

      {!loading && !error && data && (
        <div className="admin-card">
          <table className="admin-tabla">
            <thead>
              <tr>
                <th>{t('admin.usuarios_admin.col_nombre')}</th>
                <th>{t('admin.usuarios_admin.col_email')}</th>
                <th>{t('admin.usuarios_admin.col_tipo')}</th>
                <th>{t('admin.usuarios_admin.col_estado')}</th>
                <th>{t('admin.usuarios_admin.col_ultimo_login')}</th>
                <th>{t('admin.usuarios_admin.col_creado')}</th>
                <th className="admin-col-acciones">{t('admin.acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((u) => {
                const nombreCompleto = [u.nombre, u.apellidos].filter(Boolean).join(' ').trim() || '—';
                return (
                  <tr key={u.id} className={u.activo ? '' : 'admin-row-tenue'}>
                    <td>{nombreCompleto}</td>
                    <td className="admin-celda-tenue">{u.email || '—'}</td>
                    <td>
                      {u.es_super_admin
                        ? <span className="admin-pill admin-pill--sage">{t('admin.usuarios_admin.tipo_super')}</span>
                        : <span className="admin-pill admin-pill--neutro">{t('admin.usuarios_admin.tipo_admin')}</span>}
                    </td>
                    <td>
                      {u.activo
                        ? <span className="admin-pill admin-pill--sage">{t('admin.usuarios_admin.activo')}</span>
                        : <span className="admin-pill admin-pill--clay">{t('admin.usuarios_admin.inactivo')}</span>}
                    </td>
                    <td className="admin-celda-tenue">{fmtFechaHora(u.last_sign_in_at, lang)}</td>
                    <td className="admin-celda-tenue">{fmtFecha(u.created_at, lang)}</td>
                    <td className="admin-col-acciones">
                      <button type="button" className="btn-icon" title={t('admin.acciones_editar')} onClick={() => setEditing(u)}>
                        <Edit2 size={14} />
                      </button>
                      {u.es_super_admin ? (
                        <span className="admin-celda-tenue" title={t('admin.usuarios_admin.tip_super_protegido')}>—</span>
                      ) : u.activo ? (
                        <button type="button" className="btn-icon" title={t('admin.usuarios_admin.tip_desactivar')} onClick={() => handleDesactivar(u)}>
                          <Power size={14} />
                        </button>
                      ) : (
                        <button type="button" className="btn-icon" title={t('admin.usuarios_admin.tip_reactivar')} onClick={() => handleReactivar(u)}>
                          <RefreshCw size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showInvitar && (
        <ModalInvitar
          onClose={() => setShowInvitar(false)}
          onSuccess={() => { setShowInvitar(false); cargar(); setBanner({ tipo: 'ok', texto: t('admin.usuarios_admin.invitacion_enviada') }); }}
        />
      )}
      {editing && (
        <ModalEditar
          user={editing}
          onClose={() => setEditing(null)}
          onSuccess={() => { setEditing(null); cargar(); setBanner({ tipo: 'ok', texto: t('admin.usuarios_admin.editado_ok') }); }}
        />
      )}
    </div>
  );
}

function ModalInvitar({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!email.trim()) { setErr(t('admin.usuarios_admin.error_email_vacio')); return; }
    setEnviando(true);
    try {
      await invitarAdmin(email.trim());
      onSuccess();
    } catch (e2) {
      const msg = e2 instanceof ApiError
        ? (t('admin.usuarios_admin.errores.' + e2.code, { defaultValue: '' }) || e2.message)
        : t('auth.errores.generico');
      setErr(msg);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-titulo">{t('admin.usuarios_admin.modal_invitar_titulo')}</h2>
        <p className="modal-sub">{t('admin.usuarios_admin.modal_invitar_sub')}</p>
        <form onSubmit={submit} noValidate>
          <div className="modal-campo">
            <label htmlFor="inv-email">{t('admin.usuarios_admin.email')} *</label>
            <input id="inv-email" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} disabled={enviando} />
          </div>
          {err && (
            <div className="modal-error-general">
              <AlertCircle size={16} /><span>{err}</span>
            </div>
          )}
          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={enviando}>
              {t('comun.cancelar')}
            </button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? (<><Loader2 size={14} className="spin" /> {t('auth.enviando')}</>) : t('admin.usuarios_admin.btn_enviar_invitacion')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalEditar({ user, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [nombre, setNombre] = useState(user.nombre || '');
  const [apellidos, setApellidos] = useState(user.apellidos || '');
  const [idioma, setIdioma] = useState(user.idioma_preferido || 'es');
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!nombre.trim()) { setErr(t('admin.usuarios_admin.error_nombre_vacio')); return; }
    const cambios = {};
    if (nombre.trim() !== (user.nombre || '')) cambios.nombre = nombre.trim();
    if ((apellidos.trim() || null) !== (user.apellidos || null)) cambios.apellidos = apellidos.trim() || null;
    if (idioma !== user.idioma_preferido) cambios.idioma_preferido = idioma;
    if (Object.keys(cambios).length === 0) { onClose(); return; }
    setEnviando(true);
    try {
      await updateAdminUser(user.id, cambios);
      onSuccess();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? (e2.message || t('auth.errores.generico')) : t('auth.errores.generico'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-titulo">{t('admin.usuarios_admin.modal_editar_titulo')}</h2>
        <p className="modal-sub">{user.email}</p>
        <form onSubmit={submit} noValidate>
          <div className="modal-campo">
            <label>{t('admin.usuarios_admin.nombre')} *</label>
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} disabled={enviando} />
          </div>
          <div className="modal-campo">
            <label>{t('admin.usuarios_admin.apellidos')}</label>
            <input type="text" value={apellidos} onChange={(e) => setApellidos(e.target.value)} disabled={enviando} />
          </div>
          <div className="modal-campo">
            <label>{t('admin.usuarios_admin.idioma')}</label>
            <select value={idioma} onChange={(e) => setIdioma(e.target.value)} disabled={enviando}>
              <option value="es">Español</option>
              <option value="en">English</option>
              <option value="ru">Русский</option>
              <option value="fr">Français</option>
            </select>
          </div>
          {err && (
            <div className="modal-error-general"><AlertCircle size={16} /><span>{err}</span></div>
          )}
          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? (<><Loader2 size={14} className="spin" /> {t('auth.enviando')}</>) : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
